import * as z from "zod";
import { BOT_SECRET_VALUE_MAX_LENGTH } from "./bot-secrets.js";
import { Id } from "./ids.js";

/**
 * The Website teammate: a bot that keeps a site's code in GitHub and lets the host build each
 * push. Its tokens are saved in the owner's vault as command variables, so shell commands can use
 * them and the model only ever sees their names.
 */

/** Where the site is built and served. Git hosts build each push; SSH pulls on the server. */
export const WEBSITE_HOSTS = ["vercel", "netlify", "cloudflare_pages", "ssh"] as const;
export type WebsiteHost = (typeof WEBSITE_HOSTS)[number];

/** Each host's own name, the same in every language. */
export const WEBSITE_HOST_LABELS: Record<WebsiteHost, string> = {
  vercel: "Vercel",
  netlify: "Netlify",
  cloudflare_pages: "Cloudflare Pages",
  ssh: "SSH",
};

/** "/, /pricing" ⇄ ["/", "/pricing"]. */
export function parseWebsitePaths(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\s,]+/)
        .map((path) => path.trim())
        .filter(Boolean),
    ),
  ];
}

/** The vault names the teammate's tokens are saved under. */
export const WEBSITE_CREDENTIALS = {
  github: "website-github-token",
  host: "website-host-token",
  ssh: "website-ssh-key",
} as const;

/** With direct pushes on, paths still changed by pull request only; at most this many. */
export const WEBSITE_PROTECTED_PATHS_MAX = 20;

/** The starting pull-request list: the home page and pricing. */
export const WEBSITE_DEFAULT_PROTECTED_PATHS = ["/", "/pricing"] as const;

const RepoSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_.-]{1,100}\/[A-Za-z0-9_.-]{1,100}$/, "Use owner/repository");

const BranchSchema = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .regex(/^[A-Za-z0-9._/-]+$/, "Use a branch name");

const SiteSchema = z
  .string()
  .trim()
  .max(2048)
  .refine((value) => {
    try {
      const url = new URL(value);
      return url.protocol === "https:" && !url.username && !url.password;
    } catch {
      return false;
    }
  }, "Use the site's https address");

const PathSchema = z
  .string()
  .trim()
  .min(1)
  .max(300)
  .regex(/^\/[^\s]*$/, "Start each path with /");

const SshTargetSchema = z
  .string()
  .trim()
  .max(300)
  .regex(/^[A-Za-z0-9._-]+@[A-Za-z0-9.-]+(:\d{1,5})?$/, "Use user@host");

/** What the owner sees. Token values never leave the vault; only whether each one is saved. */
export const WebsiteTeammateSchema = z.object({
  botId: Id,
  siteUrl: z.string().nullable(),
  repo: z.string().nullable(),
  branch: z.string(),
  host: z.enum(WEBSITE_HOSTS).nullable(),
  sshTarget: z.string().nullable(),
  protectedPaths: z.array(z.string()),
  /** The owner lets it push to the default branch; off, every change is a pull request. */
  directPush: z.boolean(),
  githubSaved: z.boolean(),
  hostSaved: z.boolean(),
  /** The daily upkeep routine, created paused with the teammate. */
  upkeep: z.object({ routineId: Id, active: z.boolean() }).nullable(),
});
export type WebsiteTeammate = z.infer<typeof WebsiteTeammateSchema>;

const TokenSchema = z.string().min(1).max(BOT_SECRET_VALUE_MAX_LENGTH);

/** Saving the setup. A token left out keeps the one already in the vault. */
export const SaveWebsiteTeammateInput = z
  .object({
    botId: Id,
    siteUrl: SiteSchema,
    repo: RepoSchema,
    branch: BranchSchema.default("main"),
    host: z.enum(WEBSITE_HOSTS),
    sshTarget: SshTargetSchema.optional(),
    protectedPaths: z.array(PathSchema).max(WEBSITE_PROTECTED_PATHS_MAX).default([]),
    directPush: z.boolean().default(false),
    githubToken: TokenSchema.optional(),
    /** The host's API token, or for SSH the private key. */
    hostToken: TokenSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.host === "ssh" && !value.sshTarget) {
      ctx.addIssue({ code: "custom", path: ["sshTarget"], message: "SSH needs user@host" });
    }
  });
export type SaveWebsiteTeammateInput = z.infer<typeof SaveWebsiteTeammateInput>;

export const WebsiteUpkeepInput = z.object({ botId: Id, active: z.boolean() });
